import { AlertCircle, ArrowRight, Banknote, CheckCircle2, Clock, FileSignature, FileText, Loader2, Send, Upload, X } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { API, portalAuthH as authH } from "@/api";
export { API, authH };

/**
 * Helpers partagés par toutes les modales de l'ImmeublePortal (Batch #12).
 */



const TONE_COLORS = {
  indigo: { bg: "bg-indigo-50", text: "text-indigo-700", accent: "bg-indigo-600", border: "border-indigo-200" },
  blue:   { bg: "bg-blue-50",   text: "text-blue-700",   accent: "bg-blue-600",   border: "border-blue-200" },
  emerald:{ bg: "bg-emerald-50",text: "text-emerald-700",accent: "bg-emerald-600",border: "border-emerald-200" },
  violet: { bg: "bg-violet-50", text: "text-violet-700", accent: "bg-violet-600", border: "border-violet-200" },
  amber:  { bg: "bg-amber-50",  text: "text-amber-700",  accent: "bg-amber-600",  border: "border-amber-200" },
  rose:   { bg: "bg-rose-50",   text: "text-rose-700",   accent: "bg-rose-600",   border: "border-rose-200" },
  slate:  { bg: "bg-slate-50",  text: "text-slate-700",  accent: "bg-slate-600",  border: "border-slate-200" },
  cyan:   { bg: "bg-cyan-50",   text: "text-cyan-700",   accent: "bg-cyan-600",   border: "border-cyan-200" },
};

export function BaseActionModal({ title, subtitle, tone = "indigo", icon: Icon, onClose, children, footer, testId }) {
  const c = TONE_COLORS[tone] || TONE_COLORS.indigo;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid={testId}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-start gap-3 p-4 border-b ${c.border} ${c.bg}`}>
          {Icon && (
            <div className={`w-10 h-10 rounded-xl ${c.accent} flex items-center justify-center flex-shrink-0`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className={`text-base font-bold ${c.text}`}>{title}</div>
            {subtitle && <div className="text-xs text-slate-600 mt-0.5">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-white rounded-lg transition" aria-label="Fermer">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 p-3 border-t border-slate-200 bg-slate-50">{footer}</div>
        )}
      </div>
    </div>
  );
}

export function FieldLabel({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-700 mb-1">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </div>
      {children}
    </label>
  );
}

export function CheckboxChip({ label, checked, onChange, dataTestId }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`text-xs px-2.5 py-1 rounded-lg border transition inline-flex items-center gap-1 ${
        checked ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
      }`}
      data-testid={dataTestId}
    >
      {checked ? "✓" : "○"} {label}
    </button>
  );
}

// =====================================================================
//  Helper components extracted from ImmeublePortal.jsx (Batch #12 refactor)
//  Used by modals : Stripe, Bail, Mileage, etc.
// =====================================================================

export function EmptyHint({ icon: Icon, message, className = "" }) {
  return (
    <div className={`text-center py-8 px-4 bg-slate-50 rounded-xl border border-dashed border-slate-200 ${className}`}>
      <Icon className="w-7 h-7 mx-auto text-slate-300 mb-2" />
      <p className="text-xs text-slate-400 italic">{message}</p>
    </div>
  );
}

export function ModalStyles() {
  return (
    <style>{`
      .cc-input { width: 100%; padding: 0.55rem 0.75rem; border: 1px solid #e2e8f0; border-radius: 0.5rem; font-size: 0.875rem; outline: none; background-color: white; }
      .cc-input:focus { border-color: #6366f1; box-shadow: 0 0 0 1px #6366f1; }
    `}</style>
  );
}

export function StripeModeBanner() {
  // Batch #14.253 — compte de production uniquement
  return (
    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="immeuble-stripe-live-badge">
      Paiement sécurisé Stripe
    </span>
  );
}

export function TabBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-t-md transition
        ${active
          ? "bg-white border border-b-transparent border-slate-200 text-violet-700"
          : "bg-transparent text-slate-500 hover:text-slate-700"}`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">{label}</span>
      <span className="inline sm:hidden">{label.split(" ")[0]}</span>
    </button>
  );
}

export function StripePADPanel({ tenant, apt, onReload }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    institution_number: "",
    transit_number: "",
    account_number: "",
    account_holder_name: `${tenant?.first_name || ""} ${tenant?.last_name || ""}`.trim(),
    account_holder_type: "individual",
    mandate_attested: false,
    mandate_signed_date: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!tenant?.id) return;
    setLoading(true);
    axios.get(`${API}/portal/immeuble/stripe/customer/${tenant.id}`, { headers: authH() })
      .then((r) => setCustomer(r.data?.customer || null))
      .catch(() => setCustomer(null))
      .finally(() => setLoading(false));
  }, [tenant?.id]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const downloadPdf = async () => {
    try {
      const r = await axios.get(
        `${API}/portal/immeuble/stripe/pad-form-pdf?tenant_id=${tenant.id}`,
        { headers: authH(), responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mandat-pad-${tenant.last_name || tenant.id}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error("Erreur lors du téléchargement du formulaire");
    }
  };

  const saveBank = async () => {
    if (!form.mandate_attested) {
      toast.error("Vous devez attester avoir le mandat signé en main");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/portal/immeuble/stripe/pad/save-bank`, {
        tenant_id: tenant.id,
        institution_number: form.institution_number.trim(),
        transit_number: form.transit_number.trim(),
        account_number: form.account_number.trim(),
        account_holder_name: form.account_holder_name.trim(),
        account_holder_type: form.account_holder_type,
        mandate_attested: true,
        mandate_signed_date: form.mandate_signed_date || null,
      }, { headers: authH() });
      toast.success(`Compte ${r.data.bank_name || "bancaire"} •••• ${r.data.last4} enregistré`);
      setCustomer(r.data);
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur Stripe");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Supprimer le compte bancaire PAD enregistré pour ce locataire ?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/portal/immeuble/stripe/customer/${tenant.id}`, { headers: authH() });
      toast.success("Compte bancaire retiré");
      setCustomer(null);
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const dueRentPayments = (apt?.unit_rent_payments || [])
    .filter((p) => p.tenant_id === tenant.id && p.status !== "paid")
    .sort((a, b) => (a.period_year - b.period_year) || (a.period_month - b.period_month));
  const [selectedRpId, setSelectedRpId] = useState(dueRentPayments[0]?.id || "");
  const selectedRp = dueRentPayments.find((p) => p.id === selectedRpId);

  const charge = async () => {
    if (!selectedRpId) { toast.error("Sélectionnez un mois à prélever"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/portal/immeuble/stripe/pad/charge`, {
        rent_payment_id: selectedRpId,
      }, { headers: authH() });
      toast.success(`Prélèvement initié — ${r.data.amount?.toLocaleString("fr-CA")} $`, { duration: 6000 });
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur Stripe");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-center py-6 text-xs text-slate-400">Chargement…</div>;

  return (
    <div className="space-y-4" data-testid="immeuble-stripe-pad-panel">
      <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5">
            <span className="w-5 h-5 rounded-full bg-violet-600 text-white text-[10px] flex items-center justify-center font-bold">1</span>
            Formulaire d'autorisation PAD
          </div>
          <button
            onClick={downloadPdf}
            className="text-[11px] px-2.5 py-1 rounded-lg bg-white border border-violet-200 text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1"
            data-testid="immeuble-stripe-pad-download"
          >
            <FileText className="w-3 h-3" /> Télécharger le PDF
          </button>
        </div>
        <p className="text-[10px] text-slate-500 leading-snug">
          Imprimez ce formulaire, faites-le signer par votre locataire et conservez-le dans vos dossiers.
          Ce mandat papier reste votre responsabilité légale.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 p-3">
        <div className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5 mb-3">
          <span className="w-5 h-5 rounded-full bg-violet-600 text-white text-[10px] flex items-center justify-center font-bold">2</span>
          Coordonnées bancaires du locataire
        </div>

        {customer ? (
          <div className="flex items-start justify-between gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="min-w-0">
              <div className="text-xs font-semibold text-emerald-800 inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> {customer.bank_name || "Compte bancaire"} •••• {customer.last4}
              </div>
              <div className="text-[10px] text-emerald-700 mt-0.5">
                Titulaire : {customer.account_holder_name} · {customer.account_holder_type === "individual" ? "Personnel" : "Entreprise"}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                Mandat attesté le {customer.mandate_attested_at?.slice(0, 10)} — Mode {customer.stripe_mode}
              </div>
            </div>
            <button
              onClick={revoke}
              disabled={busy}
              className="p-1 rounded hover:bg-rose-100 text-rose-600 disabled:opacity-40"
              data-testid="immeuble-stripe-pad-revoke"
              title="Retirer ce compte"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="space-y-2.5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <FieldLabel label="Institution (3 ch.)" required>
                <input value={form.institution_number} onChange={(e) => upd("institution_number", e.target.value.replace(/\D/g, "").slice(0, 3))} maxLength={3} placeholder="000" className="cc-input font-mono" data-testid="immeuble-stripe-pad-inst" />
              </FieldLabel>
              <FieldLabel label="Transit (5 ch.)" required>
                <input value={form.transit_number} onChange={(e) => upd("transit_number", e.target.value.replace(/\D/g, "").slice(0, 5))} maxLength={5} placeholder="11000" className="cc-input font-mono" data-testid="immeuble-stripe-pad-transit" />
              </FieldLabel>
              <FieldLabel label="N° compte (7–12 ch.)" required>
                <input value={form.account_number} onChange={(e) => upd("account_number", e.target.value.replace(/\D/g, "").slice(0, 12))} maxLength={12} placeholder="000123456789" className="cc-input font-mono" data-testid="immeuble-stripe-pad-account" />
              </FieldLabel>
            </div>
            <FieldLabel label="Nom au compte" required>
              <input value={form.account_holder_name} onChange={(e) => upd("account_holder_name", e.target.value)} maxLength={120} className="cc-input" data-testid="immeuble-stripe-pad-holder" />
            </FieldLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <FieldLabel label="Type de compte">
                <select value={form.account_holder_type} onChange={(e) => upd("account_holder_type", e.target.value)} className="cc-input">
                  <option value="individual">Personnel</option>
                  <option value="company">Entreprise</option>
                </select>
              </FieldLabel>
              <FieldLabel label="Date du mandat signé">
                <input type="date" value={form.mandate_signed_date} onChange={(e) => upd("mandate_signed_date", e.target.value)} className="cc-input" />
              </FieldLabel>
            </div>

            <label className="flex items-start gap-2 p-2 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer">
              <input
                type="checkbox"
                checked={form.mandate_attested}
                onChange={(e) => upd("mandate_attested", e.target.checked)}
                className="mt-0.5"
                data-testid="immeuble-stripe-pad-attest"
              />
              <span className="text-[11px] text-amber-900 leading-snug">
                <b>Je confirme</b> que le mandat de prélèvement automatique (PAD) a été dûment signé
                par le locataire et transmis à mon expert. Je conserve l'original chez moi.
              </span>
            </label>

            <button
              onClick={saveBank}
              disabled={busy || !form.mandate_attested}
              className="w-full py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-40 inline-flex items-center justify-center gap-2"
              data-testid="immeuble-stripe-pad-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Enregistrer le compte bancaire
            </button>
          </div>
        )}
      </div>

      {customer && (
        <div className="rounded-xl border border-slate-200 p-3">
          <div className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5 mb-3">
            <span className="w-5 h-5 rounded-full bg-violet-600 text-white text-[10px] flex items-center justify-center font-bold">3</span>
            Prélever un mois
          </div>
          {dueRentPayments.length === 0 ? (
            <div className="text-center py-4 text-[11px] text-slate-400 italic">
              Aucun mois en attente de paiement.
            </div>
          ) : (
            <div className="space-y-2">
              <FieldLabel label="Mois à prélever">
                <select
                  value={selectedRpId}
                  onChange={(e) => setSelectedRpId(e.target.value)}
                  className="cc-input"
                  data-testid="immeuble-stripe-pad-month"
                >
                  {dueRentPayments.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.period_label} — {p.amount_due?.toLocaleString("fr-CA")} $ · {p.status === "late" ? "EN RETARD" : "À payer"}
                    </option>
                  ))}
                </select>
              </FieldLabel>
              <button
                onClick={charge}
                disabled={busy || !selectedRpId}
                className="w-full py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center justify-center gap-2"
                data-testid="immeuble-stripe-pad-charge"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />}
                Prélever {selectedRp?.amount_due?.toLocaleString("fr-CA")} $
              </button>
              <p className="text-[10px] text-slate-500 italic text-center">
                Délai ACSS standard : 3 à 5 jours ouvrables. Le statut « payé » s'active automatiquement à la confirmation Stripe.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function StripeCheckoutPanel({ tenant, apt, onReload }) {
  const dueRentPayments = (apt?.unit_rent_payments || [])
    .filter((p) => p.tenant_id === tenant.id && p.status !== "paid")
    .sort((a, b) => (a.period_year - b.period_year) || (a.period_month - b.period_month));
  const [selectedRpId, setSelectedRpId] = useState(dueRentPayments[0]?.id || "");
  const [customMessage, setCustomMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const selectedRp = dueRentPayments.find((p) => p.id === selectedRpId);

  const send = async () => {
    if (!tenant.email) { toast.error("Aucun courriel renseigné pour ce locataire"); return; }
    if (!selectedRpId) { toast.error("Sélectionnez un mois à facturer"); return; }
    setBusy(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/portal/immeuble/stripe/checkout/send-link`, {
        rent_payment_id: selectedRpId,
        custom_message: customMessage,
      }, { headers: authH() });
      setResult(r.data);
      if (r.data.email_sent) {
        toast.success(`Courriel envoyé à ${r.data.to}`);
      } else {
        toast.error(`Lien créé mais envoi du courriel échoué : ${r.data.email_info || "raison inconnue"}`);
      }
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur Stripe");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="immeuble-stripe-card-panel">
      <div className="rounded-xl bg-violet-50 border border-violet-200 p-3 text-[11px] leading-snug text-violet-900">
        <b>Comment ça marche :</b> un courriel sera envoyé au locataire depuis
        <b> DGFiscalFlow@dgcomptabilite.ca</b> avec un lien Stripe Checkout sécurisé.
        Le statut du mois passera automatiquement à « payé » dès le paiement effectué.
      </div>

      {!tenant.email && (
        <div className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-[11px] text-rose-700">
          Ce locataire n'a pas d'adresse courriel renseignée. Ajoutez-en une via l'édition du locataire.
        </div>
      )}

      <FieldLabel label="Locataire (destinataire)">
        <div className="cc-input bg-slate-50 text-slate-500" data-testid="immeuble-stripe-card-tenant-readonly">
          {tenant.first_name} {tenant.last_name} {tenant.email ? `· ${tenant.email}` : ""}
        </div>
      </FieldLabel>

      <FieldLabel label="Mois à facturer" required>
        {dueRentPayments.length === 0 ? (
          <div className="cc-input bg-slate-50 text-slate-400 italic">Aucun mois en attente</div>
        ) : (
          <select
            value={selectedRpId}
            onChange={(e) => setSelectedRpId(e.target.value)}
            className="cc-input"
            data-testid="immeuble-stripe-card-month"
          >
            {dueRentPayments.map((p) => (
              <option key={p.id} value={p.id}>
                {p.period_label} — {p.amount_due?.toLocaleString("fr-CA")} $
              </option>
            ))}
          </select>
        )}
      </FieldLabel>

      <FieldLabel label="Message personnel (optionnel)">
        <textarea
          value={customMessage}
          onChange={(e) => setCustomMessage(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="ex: Merci de régler avant le 5 du mois."
          className="cc-input"
          data-testid="immeuble-stripe-card-message"
        />
      </FieldLabel>

      <button
        onClick={send}
        disabled={busy || !tenant.email || !selectedRpId}
        className="w-full py-2.5 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-40 inline-flex items-center justify-center gap-2"
        data-testid="immeuble-stripe-card-send"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        Envoyer le lien de paiement ({selectedRp?.amount_due?.toLocaleString("fr-CA")} $)
      </button>

      {result && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-[11px] space-y-1.5">
          <div className="font-semibold text-slate-700">Lien généré :</div>
          <div className="font-mono text-[10px] text-slate-500 break-all bg-white px-2 py-1 rounded border">
            {result.url}
          </div>
          {result.email_sent
            ? <div className="text-emerald-700">Courriel envoyé à <b>{result.to}</b></div>
            : <div className="text-rose-700">Envoi courriel échoué — copiez le lien ci-dessus et transmettez-le manuellement.</div>}
        </div>
      )}
    </div>
  );
}

const LEASE_PROCESSING_STEPS = [
  { id: "queued",              label: "En file d'attente",         icon: Clock },
  { id: "generating_pdf",      label: "Génération PDF...",         icon: FileText },
  { id: "uploading_drive",     label: "Archivage sécurisé...",     icon: Upload },
  { id: "sending_emails",      label: "Envoi des courriels...",    icon: Send },
  { id: "initiating_signature", label: "Liens magiques aux locataires...", icon: FileSignature },
  { id: "completed",           label: "Terminé !",                 icon: CheckCircle2 },
];

export function LeaseProcessingProgress({ status, error, leaseId }) {
  const currentIdx = LEASE_PROCESSING_STEPS.findIndex((s) => s.id === status);
  const isCompleted = status === "completed";
  const isFailed = status === "failed";
  const totalSteps = LEASE_PROCESSING_STEPS.length;
  const progressPct = isCompleted ? 100 : isFailed ? 100 : Math.max(15, ((currentIdx + 1) / totalSteps) * 100);

  if (!status || status === "completed") {
    return (
      <div
        className={`rounded-xl border p-3 text-[12px] ${isCompleted ? "bg-emerald-50 border-emerald-200" : "bg-blue-50 border-blue-200"}`}
        data-testid="immeuble-lease-progress"
      >
        <div className={`font-semibold inline-flex items-center gap-1.5 ${isCompleted ? "text-emerald-800" : "text-blue-800"}`}>
          <CheckCircle2 className="w-3.5 h-3.5" />
          {isCompleted ? "Bail traité avec succès !" : "Bail créé !"}
        </div>
        <p className={`text-[11px] mt-1 ${isCompleted ? "text-emerald-700" : "text-blue-700"}`}>
          {isCompleted
            ? "PDF généré · archivé en sécurité · liens magiques envoyés. Cliquez « Télécharger le bail PDF » pour consulter."
            : "Cliquez « Télécharger le bail PDF » pour récupérer le formulaire officiel TAL pré-rempli."}
        </p>
      </div>
    );
  }

  if (isFailed) {
    return (
      <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-[12px]" data-testid="immeuble-lease-progress">
        <div className="font-semibold text-rose-800 inline-flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" /> Erreur lors du traitement
        </div>
        <p className="text-[11px] text-rose-700 mt-1">{error || "Une erreur est survenue. Le bail est créé mais le PDF n'a pas pu être archivé."}</p>
        <p className="text-[10px] text-rose-600 mt-1 font-mono">Ref : {leaseId?.slice(0, 8)}</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-blue-50 border-2 border-blue-200 p-3.5 space-y-2.5" data-testid="immeuble-lease-progress">
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-bold text-blue-900 inline-flex items-center gap-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Traitement en cours...
        </span>
        <span className="text-[10px] text-blue-700 font-mono">{Math.round(progressPct)}%</span>
      </div>

      {/* Barre de progression */}
      <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Liste des étapes */}
      <div className="space-y-1">
        {LEASE_PROCESSING_STEPS.slice(0, -1).map((step, idx) => {
          const Icon = step.icon;
          const isDone = idx < currentIdx;
          const isCurrent = idx === currentIdx;
          return (
            <div
              key={step.id}
              className={`flex items-center gap-2 text-[11px] transition ${
                isDone ? "text-emerald-700" : isCurrent ? "text-blue-800 font-semibold" : "text-slate-400"
              }`}
              data-testid={`immeuble-lease-step-${step.id}`}
            >
              {isDone ? (
                <CheckCircle2 className="w-3 h-3 flex-shrink-0 text-emerald-500" />
              ) : isCurrent ? (
                <Loader2 className="w-3 h-3 flex-shrink-0 animate-spin" />
              ) : (
                <Icon className="w-3 h-3 flex-shrink-0 opacity-50" />
              )}
              <span>{step.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

